import React from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { isEmpty, isNil, noop } from 'lodash';

import type { ChatInputFieldProps } from '~/components/AiChat/AiChatContainer/ChatInputField';
import { ChatInputField } from '~/components/AiChat/AiChatContainer/ChatInputField';
import { ChatLoadingMessage } from '~/components/AiChat/AiChatContainer/ChatLoadingMessage';
import { WelcomeScreen } from '~/components/AiChat/AiChatContainer/WelcomeScreen';
import { ChatErrorState } from '~/components/AiChat/components/ChatErrorState';
import { AiGreeting } from '~/components/AiChat/components/Message/AiGreeting';
import { AiMessage } from '~/components/AiChat/components/Message/AiMessage';
import { HumanMessage } from '~/components/AiChat/components/Message/HumanMessage';
import { ChatSnackbarContext } from '~/components/AiChat/components/Message/MessageLayout';
import { CHAT_V2_LAYOUT } from '~/components/AiChat/constants/chatStyling';
import { useChatController } from '~/components/AiChat/controller/ChatControllerContext';
import type { ChatExchangeView } from '~/components/AiChat/controller/types';
import { useChatMixpanel } from '~/components/AiChat/hooks/useChatMixpanel';
import type { ListItem } from '~/components/core/Molecules/OverflowList/Chips';
import { OverflowChipsList } from '~/components/core/Molecules/OverflowList/Chips';
import { FeedbackSnackbar, useSnackbar } from '~/components/core/Molecules/Snackbar';
import FileIcon from '~/components/icons/FileIcon';
import { CLIVE_CHAT_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import cn from '~/Utils/cn';

export interface ChatInputRenderProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => Promise<void>;
  onPromptSubmit: (promptTitle: string, promptKey: string) => Promise<void>;
  disabled: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
}

export interface AiChatContainerProps {
  className?: string;
  messagesContainerClassName?: string;
  showGreetingAsMessage?: boolean;
  inputFieldType?: ChatInputFieldProps['type'];
  /** Required outside a claim: without it the chat falls back to the legacy ChatInputField, which reads the claim. */
  renderInput?: (props: ChatInputRenderProps) => React.ReactNode;
}

const NO_DOCUMENT_IDS: number[] = [];

// Declared once, so the memoized Virtuoso `components` keep a single Header; it reads the greeting from
// the controller.
const GreetingHeader: React.FC = () => {
  const { greeting } = useChatController({ required: true }).presentation;
  return (
    <div className={cn('mx-auto flex w-full flex-col items-center', CHAT_V2_LAYOUT.maxWidthClass)}>
      <AiGreeting text={greeting} />
    </div>
  );
};

export const AiChatContainerComponent: React.FC<AiChatContainerProps> = ({
  className,
  messagesContainerClassName,
  showGreetingAsMessage = true,
  inputFieldType = 'elevated',
  renderInput,
}) => {
  const {
    conversationId,
    loadState,
    exchanges,
    activeExchange,
    isAnswering,
    showWelcome,
    send,
    presentation,
    sendFeedback,
    regenerate,
    claim,
  } = useChatController({ required: true });
  const isTurnFailed = activeExchange?.status === 'failed';
  const selectedDocumentIds = claim?.selectedDocumentIds ?? NO_DOCUMENT_IDS;
  const { trackChatEvent } = useChatMixpanel();
  const [userInput, setUserInput] = React.useState('');
  const [pendingQuestion, setPendingQuestion] = React.useState<{ text: string; documentIds: number[] } | null>(null);
  const [isLastMessageNew, setIsLastMessageNew] = React.useState<boolean>(false);
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

  const [currentExchanges, setCurrentExchanges] = React.useState<ChatExchangeView[]>(exchanges);
  const chatDocuments: ListItem[] = (claim?.documents ?? [])
    .filter((document) => selectedDocumentIds.includes(document.id))
    .map((doc) => ({
      id: doc.id,
      chipProps: {
        label: doc.document_name,
        size: 'small',
        variant: 'outlined',
        icon: <FileIcon className="text-slate-700" iconColor="currentColor" />,
        className: 'pl-4',
        onDelete: () => {
          trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_DOCUMENT_CHIP_REMOVED, { document_id: doc.id });
          const newChatDocumentIds = selectedDocumentIds.filter((id) => id !== doc.id);
          claim?.setSelectedDocumentIds(newChatDocumentIds);
        },
      },
    }));

  React.useEffect(() => {
    setCurrentExchanges(exchanges);
  }, [exchanges]);

  const isLoaded = loadState === 'ready';
  const activeExchangeError = activeExchange?.error ?? null;
  const displayExchanges = React.useMemo(() => {
    if (!isLoaded) return [];
    const sorted = [...currentExchanges].sort(
      (a, b) => new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime()
    );
    const optimisticExchange: ChatExchangeView | null = pendingQuestion
      ? {
          id: '0',
          userInput: pendingQuestion.text,
          status: 'processing',
          response: null,
          error: null,
          createdAt: new Date().toISOString(),
          responseTimeMs: null,
        }
      : null;
    const rows = optimisticExchange ? [...sorted, optimisticExchange] : sorted;
    if (!isTurnFailed || isEmpty(rows)) return rows;

    // The failed turn is the one the chat was answering, shown last: its optimistic question, or its
    // own row when the chat re-attached to it after a reload.
    const lastRow = rows[rows.length - 1];
    return [...rows.slice(0, -1), { ...lastRow, status: 'failed' as const, error: activeExchangeError }];
  }, [isLoaded, currentExchanges, pendingQuestion, isTurnFailed, activeExchangeError]);

  const virtuosoRef = React.useRef<VirtuosoHandle>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = React.useCallback(() => {
    setTimeout(() => {
      virtuosoRef.current?.scrollToIndex({ index: 'LAST', behavior: 'auto' });
    }, 50);
  }, []);

  const handleRegenerateResponse = React.useCallback(async () => {
    const lastExchange = currentExchanges[currentExchanges.length - 1];
    setCurrentExchanges(exchanges.slice(0, -1));
    setPendingQuestion({
      text: lastExchange.userInput,
      documentIds: claim?.contextDocumentIdsFor(lastExchange.id) ?? NO_DOCUMENT_IDS,
    });

    await regenerate?.(lastExchange.id);
  }, [currentExchanges, exchanges, claim, regenerate]);

  const handleTypeEnd = React.useCallback(() => setIsLastMessageNew(false), []);
  const virtuosoComponents = React.useMemo(
    () => (showGreetingAsMessage ? { Header: GreetingHeader } : undefined),
    [showGreetingAsMessage]
  );

  // ChatPanel passes renderInput inline, so its identity changes on every parent render, including
  // each streamed agent event. Key on whether a custom input is rendered instead.
  const hasRenderInput = !!renderInput;

  React.useEffect(() => {
    scrollToBottom();
    const activeRef = hasRenderInput ? textareaRef : inputRef;
    if (activeRef.current) {
      activeRef.current.focus();
    }
  }, [displayExchanges.length, conversationId, hasRenderInput, scrollToBottom]);

  // Scroll once when the agent starts on an exchange (a regenerate, retry or reload leaves the list
  // length unchanged, so nothing else would), but not on each streamed agent event: the thinking
  // section has to stay put while it is being read.
  React.useEffect(() => {
    if (isAnswering) {
      scrollToBottom();
    }
  }, [isAnswering, scrollToBottom]);

  const handleUserInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput(event.target.value);
  };

  React.useEffect(() => {
    if (!isAnswering && !isTurnFailed) {
      setPendingQuestion(null);
    }
  }, [isAnswering, isTurnFailed]);

  const handleQuestionSubmit = async (questionText: string) => {
    setPendingQuestion({ text: questionText, documentIds: selectedDocumentIds });
    setIsLastMessageNew(true);
    setUserInput('');
    try {
      await send(questionText);
    } catch (error) {
      setIsLastMessageNew(false);
      // The turn was not accepted: the question goes back to the input, to send again.
      setUserInput(questionText);
    }
  };

  const handleUserSubmit = async () => {
    if (userInput) {
      await handleQuestionSubmit(userInput);
    }
  };

  const handlePromptSubmit = async (promptTitle: string, promptKey: string) => {
    // Predefined prompts belong to a claim's chat; checked before the question shows, so it cannot wait on a
    // prompt that is never sent.
    if (!claim) return;
    setPendingQuestion({ text: promptTitle, documentIds: selectedDocumentIds });
    setIsLastMessageNew(true);
    try {
      await claim.sendPrompt(promptKey);
    } catch (error) {
      setIsLastMessageNew(false);
    }
  };

  const isChatEmpty = isEmpty(displayExchanges);

  const isInputPositionTop = isChatEmpty && !showGreetingAsMessage;
  const showWelcomeScreen = showWelcome && !pendingQuestion;

  if (!isLoaded) {
    if (loadState === 'error') {
      return (
        <ChatErrorState className={cn('flex h-full w-full flex-col items-center justify-center gap-16', className)} />
      );
    }

    return (
      <div className={cn('flex h-full w-full flex-col overflow-hidden', className)}>
        <div className={cn('flex min-h-0 w-full flex-1 flex-col overflow-auto', messagesContainerClassName)}>
          <ChatLoadingMessage assistantName={presentation.assistantName} />
        </div>
        <div className="shrink-0">
          {renderInput
            ? renderInput({
                value: '',
                onChange: () => {},
                onSubmit: async () => {},
                onPromptSubmit: async () => {},
                disabled: true,
                inputRef: textareaRef,
              })
            : null}
        </div>
      </div>
    );
  }

  // Welcome screen with centered input
  if (showWelcomeScreen && renderInput) {
    return (
      <div className={cn('flex h-full w-full flex-col overflow-hidden', className)}>
        <WelcomeScreen assistantName={presentation.assistantName} greeting={presentation.greeting}>
          {renderInput({
            value: userInput,
            onChange: setUserInput,
            onSubmit: handleUserSubmit,
            onPromptSubmit: handlePromptSubmit,
            disabled: isAnswering,
            inputRef: textareaRef,
          })}
        </WelcomeScreen>
      </div>
    );
  }

  // Chat layout with messages
  return (
    <div className={cn('flex h-full w-full flex-col overflow-hidden', className)}>
      <ChatSnackbarContext.Provider value={showSnackbar}>
        <Virtuoso
          ref={virtuosoRef}
          data={displayExchanges}
          className={cn('min-h-0 w-full flex-1', messagesContainerClassName)}
          // While a response streams, don't let Virtuoso pin a reader at the bottom to the growing
          // thinking section.
          followOutput={isAnswering ? false : 'auto'}
          initialTopMostItemIndex={Math.max(0, displayExchanges.length - 1)}
          increaseViewportBy={400} // pre-render 400px above/below viewport to avoid flicker on fast scroll
          components={virtuosoComponents}
          itemContent={(index, exchange) => {
            const isLast = index === displayExchanges.length - 1;
            // The optimistic question is always the last row, and the controller does not hold it yet.
            const contextDocumentIds =
              isLast && !isNil(pendingQuestion)
                ? pendingQuestion.documentIds
                : (claim?.contextDocumentIdsFor(exchange.id) ?? NO_DOCUMENT_IDS);
            return (
              <div className={cn('mx-auto flex w-full flex-col items-center gap-8', CHAT_V2_LAYOUT.maxWidthClass)}>
                <HumanMessage exchange={exchange} contextDocumentIds={contextDocumentIds} />
                <AiMessage
                  exchange={exchange}
                  onUserFeedback={sendFeedback}
                  withTypeEffect={isLast && isLastMessageNew}
                  onTypeEnd={isLast ? handleTypeEnd : noop}
                  onRegenerate={regenerate ? (isLast ? handleRegenerateResponse : noop) : undefined}
                  showDisclaimer={isLast}
                  isLast={isLast}
                />
              </div>
            );
          }}
        />
      </ChatSnackbarContext.Provider>
      <div
        className={cn('shrink-0', {
          'mb-16': inputFieldType === 'elevated',
          'px-16': isInputPositionTop,
        })}
      >
        <div
          className={cn(
            'block',
            !isEmpty(chatDocuments) ? 'px-12 pt-12' : '',
            `mx-auto w-full ${CHAT_V2_LAYOUT.maxWidthClass}`
          )}
        >
          <OverflowChipsList
            items={chatDocuments}
            overflowChipProps={{
              size: 'small',
              variant: 'outlined',
            }}
          />
        </div>

        {renderInput ? (
          renderInput({
            value: userInput,
            onChange: setUserInput,
            onSubmit: handleUserSubmit,
            onPromptSubmit: handlePromptSubmit,
            disabled: isAnswering,
            inputRef: textareaRef,
          })
        ) : (
          <ChatInputField
            onUserInput={handleUserInput}
            onUserSubmit={handleUserSubmit}
            onPromptSubmit={handlePromptSubmit}
            disabled={isAnswering}
            inputRef={inputRef}
            value={userInput}
            type={isInputPositionTop ? 'elevated' : inputFieldType}
          />
        )}
      </div>
      <FeedbackSnackbar state={snackbarState} onClose={closeSnackbar} />
    </div>
  );
};

export const AiChatContainer = React.memo(AiChatContainerComponent);
